import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import type { CommunityMember } from '@zealy/queries';
import { Alert, Button, TaskCard } from '@zealy/design-system';
import { RefreshCwLine, TwitterFilled } from '@zealy/icons';
import { apiV2, useAuthenticatedUser } from '@zealy/queries';

import { useError } from '#app/cw/[subdomain]/(app)/questboard/admin/[questId]/_components/FormError.context';
import { toast } from '#components/Toaster';
import { TASK_CONFIG } from '#constants/quests/Task.constants';
import { useOauth2 } from '#hooks/useOAuth2';

import type { QuestTask } from '../Tasks.types';
import { withQuest } from '../Tasks.funcs';

type WrapperProps = Extract<QuestTask, { type: 'twitterBlue' }>;

const type = 'twitterBlue' as const;
type Task = typeof type;

export const TwitterBlue = ({
  task,
  preview = false,
}: { task: WrapperProps } & { preview?: boolean }) => {
  const twitterAuth = useOauth2('twitter');
  const user = useAuthenticatedUser<CommunityMember>();
  const tConnect = useTranslations('settings.linked-accounts');

  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get user's Twitter account info
  const accounts = user.data && 'accounts' in user.data ? user.data.accounts : [];
  const twitterAccount = accounts?.find(a => a.accountType === 'twitter');
  const hasTwitterConnected = !!twitterAccount && twitterAccount.tokenStatus === 'valid';
  const hasTwitterPremium = hasTwitterConnected && twitterAccount.premiumAccount;
  const premiumAccountNotSet = hasTwitterConnected && !('premiumAccount' in twitterAccount);

  const { clearError, error, setError } = useError(
    `${task.id}.twitterBlue`,
    !preview && task.id && !hasTwitterPremium ? 'Twitter Blue Required' : undefined,
  );

  useEffect(() => {
    if (!task.id || preview) return;

    if (error && hasTwitterPremium) {
      clearError();
    } else if (!error && !hasTwitterPremium && hasTwitterConnected) {
      setError('Twitter Blue Required');
    }
  }, [hasTwitterPremium, hasTwitterConnected, task.id, setError, error, clearError, preview]);

  const handleRefreshStatus = async () => {
    setIsRefreshing(true);
    try {
      const response = await apiV2.auth.checkTwitterPremiumStatus();

      if (response.status === 200) {
        toast.success('Twitter status refreshed successfully');
        // Refetch user data to get updated premium status
        user.refetch();
      }
    } catch (error) {
      console.error('Error refreshing Twitter status:', error);
      toast.error('Failed to refresh Twitter status. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // If Twitter is not connected, show connect button
  if (!hasTwitterConnected || preview) {
    return (
      <TaskCard
        title={`${tConnect('connect')} ${tConnect('twitter.title')}`}
        type="twitter"
        className="px-200 py-300"
        icon={TASK_CONFIG.twitterBlue.icon}
      >
        <Button
          leftIcon={<TwitterFilled />}
          size="lg"
          className="w-full text-white bg-task-type-twitter hover:bg-task-type-twitter-hover active:bg-task-type-twitter-press"
          onClick={() => twitterAuth.open()}
        >
          {tConnect('connect')} {tConnect('twitter.title')}
        </Button>
      </TaskCard>
    );
  }

  // If Twitter is connected but premium status is unknown, show check button
  if (premiumAccountNotSet) {
    return (
      <Alert
        variant="info"
        title="Verify Twitter Blue Status"
        description="We need to verify if your connected Twitter account has a premium subscription. Click the button below to check your Twitter Blue status."
        actions={
          <Button
            variant="muted"
            size="sm"
            leftIcon={<RefreshCwLine />}
            onClick={handleRefreshStatus}
            loading={isRefreshing}
          >
            Check Twitter Blue Status
          </Button>
        }
      />
    );
  }

  // If Twitter is connected but doesn't have premium, show warning with refresh option
  if (!hasTwitterPremium) {
    return (
      <Alert
        variant="warning"
        title="Twitter Blue Required"
        description="Your connected Twitter account doesn't have a premium subscription. You need Twitter Blue to complete this task. If you've recently upgraded to Twitter Blue, click refresh to update your status."
        actions={
          <Button
            variant="muted"
            size="sm"
            leftIcon={<RefreshCwLine />}
            onClick={handleRefreshStatus}
            loading={isRefreshing}
          >
            Refresh Status
          </Button>
        }
      />
    );
  }

  // If user has Twitter Blue, show success state (this should allow task completion)
  return (
    <Alert
      variant="success"
      title="Twitter Blue Verified"
      description="Your Twitter Blue status has been verified!"
    />
  );
};

const TwitterBlueClaim = withQuest<WrapperProps, Task>(({ task }) => {
  const { field } = useController({
    name: task.id,
    defaultValue: {
      taskId: task.id,
      type,
    },
  });

  return <TwitterBlue task={task} />;
});

export default TwitterBlueClaim;
